import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, WifiOff } from 'lucide-react';
import type { DeadLetterPageDto, NphiesPollResultDto, NphiesPollStatusDto } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { visibleInterval } from '@/lib/polling';
import { dateTime, relative } from '@/lib/utils';
import { DataTable } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, DatePicker } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

const STATUS_POLL_MS = 60_000;

function useCountdown(seconds: number): number {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    setLeft(seconds);
    if (seconds <= 0) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      setLeft((current) => Math.max(0, current - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [seconds]);
  return left;
}

export function usePollStatus(live = true) {
  const { can, hasModule } = useAuth();
  const enabled = hasModule('nphies') && can('nphies.messages.read', 'claim.read', 'preauth.manage');
  return {
    enabled,
    query: useQuery({
      queryKey: ['nphies-poll'],
      queryFn: () => get<NphiesPollStatusDto>('/integrations/nphies/poll/status'),
      enabled,
      refetchInterval: live ? visibleInterval(STATUS_POLL_MS) : false,
      staleTime: 30_000,
    }),
  };
}

function DeadLetters() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ['nphies-dead-letters'],
    queryFn: () => get<DeadLetterPageDto>(`/integrations/nphies/dead-letters${qs({ limit: 20 })}`),
  });
  const retry = useMutation({
    mutationFn: (id: string) => post<{ resolved: boolean; message: string }>(`/integrations/nphies/dead-letters/${id}/retry`, {}),
    onSuccess: (result) => {
      if (result.resolved) toast.success('Applied', result.message);
      else toast.warning('Still not applied', result.message);
      void queryClient.invalidateQueries({ queryKey: ['nphies-dead-letters'] });
      void queryClient.invalidateQueries({ queryKey: ['nphies-poll'] });
      void queryClient.invalidateQueries({ queryKey: ['claims'] });
      void queryClient.invalidateQueries({ queryKey: ['pre-auth-tab'] });
    },
    onError: (error) => toast.error('Could not apply it', describeError(error)),
  });

  return (
    <div className="mt-3 space-y-2">
      <p className="text-sm font-medium">Messages from the insurer that could not be applied</p>
      <p className="text-xs text-muted-foreground">They are kept for 14 days so nothing is lost. Apply one again after fixing what was missing.</p>
      <DataTable
        dense
        rows={list.data?.items}
        loading={list.isLoading} refreshing={list.isFetching}
        error={list.error}
        rowKey={(row) => row.id}
        empty="Nothing waiting."
        columns={[
          { header: 'Received', sort: (row) => row.receivedAt, cell: (row) => <span className="whitespace-nowrap text-xs">{dateTime(row.receivedAt)}</span> },
          { header: 'Kind', sort: (row) => row.eventKind, cell: (row) => row.eventKind },
          { header: 'Why', sort: (row) => row.reason, cell: (row) => <span className="text-xs text-muted-foreground">{row.reason}</span> },
          { header: 'Tries', align: 'right', sort: (row) => row.attempts, cell: (row) => row.attempts },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <Button size="sm" variant="outline" disabled={!row.retryable} loading={retry.isPending && retry.variables === row.id} onClick={() => retry.mutate(row.id)}>
                {row.retryable ? 'Apply again' : 'Too large to keep'}
              </Button>
            ),
          },
        ]}
      />
    </div>
  );
}

export function PollPanel({ live = true }: { live?: boolean }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { enabled, query } = usePollStatus(live);
  const status = query.data;
  const waiting = useCountdown(status?.manualRetryInSeconds ?? 0);

  const check = useMutation({
    mutationFn: () => post<NphiesPollResultDto>('/integrations/nphies/poll/now', { mode: 'all' }),
    onSuccess: (result) => {
      if (result.status === 'ok') toast.success('Checked the insurer', result.message);
      else if (result.status === 'offline') toast.warning('No internet connection', result.message);
      else toast.warning('Not checked', result.message);
      void queryClient.invalidateQueries({ queryKey: ['nphies-poll'] });
      void queryClient.invalidateQueries({ queryKey: ['nphies-dead-letters'] });
      if (result.delivered > 0) {
        void queryClient.invalidateQueries({ queryKey: ['claims'] });
        void queryClient.invalidateQueries({ queryKey: ['pre-auth-tab'] });
        void queryClient.invalidateQueries({ queryKey: ['pre-auth-counts'] });
        void queryClient.invalidateQueries({ queryKey: ['nphies-communications'] });
        void queryClient.invalidateQueries({ queryKey: ['reconciliations'] });
      }
    },
    onError: (error) => toast.error('Could not check the insurer', describeError(error)),
  });

  const [catchingUp, setCatchingUp] = useState(false);
  const [catchFrom, setCatchFrom] = useState('');
  const [catchTo, setCatchTo] = useState('');
  const catchUp = useMutation({
    mutationFn: () => post<NphiesPollResultDto>('/integrations/nphies/poll/catch-up', { from: catchFrom, to: catchTo }),
    onSuccess: (result) => {
      if (result.status === 'ok') toast.success('Caught up with the insurer', result.message);
      else toast.warning('Not caught up', result.message);
      setCatchingUp(false);
      void queryClient.invalidateQueries({ queryKey: ['nphies-poll'] });
      void queryClient.invalidateQueries({ queryKey: ['nphies-inbound'] });
    },
    onError: (error) => toast.error('Could not catch up', describeError(error)),
  });

  if (!enabled || !status) return null;
  const counts = status.lastCounts;
  const waitingOn = status.pending.claims + status.pending.preAuths;

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row flex-wrap items-center gap-2">
        <CardTitle className="text-sm">Answers from the insurer</CardTitle>
        {waitingOn > 0 ? <Badge variant="warning">{waitingOn} waiting for an answer</Badge> : <Badge variant="secondary">Nothing waiting</Badge>}
        {status.communications.open > 0 && <Badge variant={status.communications.overdue > 0 ? 'destructive' : 'warning'}>{status.communications.open} insurer request{status.communications.open === 1 ? '' : 's'} to answer</Badge>}
        {status.deadLetters.open > 0 && <Badge variant="destructive">{status.deadLetters.open} could not be applied</Badge>}
        {(status.inboundApprovals ?? 0) > 0 && <Badge variant="warning">{status.inboundApprovals} approval{status.inboundApprovals === 1 ? '' : 's'} sent by insurers to file</Badge>}
        {can('nphies.poll.now') && (
          <Button className="ms-auto gap-1.5" size="sm" variant="outline" loading={check.isPending} disabled={waiting > 0} onClick={() => check.mutate()}>
            <RefreshCw className="h-3.5 w-3.5" /> {waiting > 0 ? `Check again in ${waiting}s` : 'Check now'}
          </Button>
        )}
        {can('nphies.poll.now') && (
          <Button size="sm" variant="ghost" onClick={() => setCatchingUp(!catchingUp)}>
            Catch up on a past week
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {catchingUp && (
          <div className="flex flex-wrap items-end gap-2 rounded-md border p-2">
            <label className="text-xs">
              From
              <DatePicker value={catchFrom} onChange={(value) => setCatchFrom(value)} />
            </label>
            <label className="text-xs">
              To
              <DatePicker value={catchTo} onChange={(value) => setCatchTo(value)} />
            </label>
            <Button size="sm" loading={catchUp.isPending} disabled={!catchFrom || !catchTo} onClick={() => catchUp.mutate()}>
              Ask the insurer again
            </Button>
            <span className="text-xs text-muted-foreground">Up to 7 days, once an hour.</span>
          </div>
        )}
        {status.waitingForConnection && (
          <Alert variant="warning">
            <span className="inline-flex items-center gap-1.5 font-medium">
              <WifiOff className="h-4 w-4" /> Waiting for the internet connection to the insurer
            </span>{' '}
            — nothing is lost. Requests are kept and checks resume by themselves when the connection is back.
          </Alert>
        )}
        {status.lastStatus === 'failed' && status.lastError && <Alert variant="destructive">The last check failed: {status.lastError}</Alert>}
        <p className="text-muted-foreground">
          {status.lastRunAt ? `Last checked ${relative(status.lastRunAt)}` : 'Not checked yet'}
          {status.nextRunAt ? ` · next automatic check ${relative(status.nextRunAt)}` : ''}
          {` · checked every ${status.intervalMinutes} minutes`}
        </p>
        {status.lastChangeAt && (
          <p className="text-xs text-muted-foreground">
            Last time something came back ({relative(status.lastChangeAt)}): {counts.claimResponses} claim answers, {counts.preAuthResponses} pre-authorisation answers, {counts.reconciliations} payments,{' '}
            {counts.communications} requests{counts.unmatched > 0 ? `, ${counts.unmatched} that matched nothing here` : ''}.
          </p>
        )}
        {status.deadLetters.open > 0 && can('nphies.deadletter.apply', 'nphies.messages.read') && <DeadLetters />}
      </CardContent>
    </Card>
  );
}
